import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getHboActivationStatus } from 'services/api/hbo/status';
import { isHboSubscriptionActive } from '../utils/isHboSubscriptionActive';
import { useHboActivationFlag } from './useHboActivationFlag';
import { getRegion } from 'utils/Region';


const STATUS_UNLINKED = 'unlinked';
const STATUS_PENDING = 'pending';
const STATUS_LINKED = 'linked';
const STATUS_NOT_LINKED = 'not_linked';

const INITIAL_STATE = {
  loading: false,
  status: null,
  showActivationModal: false,
  permanentlySuppressed: false,
};

const LINKED_STATE = {
  loading: false,
  status: STATUS_LINKED,
  showActivationModal: false,
  permanentlySuppressed: false,
};

const normalizeStatus = status => {
  if (status === STATUS_NOT_LINKED) return STATUS_UNLINKED;

  return status;
};

// WEBCL-3778: una vez confirmado 'linked' no se vuelve a consultar /status en el resto de
// la sesion (persiste entre montajes de NodesLayout/player.provider; solo se resetea con
// un reload completo de la pagina)
let hasConfirmedLinkedInSession = false;

// SOLO PARA TESTS: el flag vive a nivel de modulo (persiste mientras el bundle este
// cargado), asi que los tests deben poder reiniciarlo entre casos para aislarse entre si.
export const __resetConfirmedLinkedForTests = () => {
  hasConfirmedLinkedInSession = false;
};

const buildStateFromResponse = ({ data, httpStatus }) => {
  if (httpStatus !== 200 || !data) {
    return INITIAL_STATE;
  }

  return {
    loading: false,
    status: normalizeStatus(data?.data?.status),
    showActivationModal: Boolean(data?.data?.show_activation_modal),
    permanentlySuppressed: Boolean(data?.data?.activation_permanently_suppressed),
  };
};

// Deriva que componente corresponde segun la tabla de estados de TBRFRE-1790 §6
const getActiveComponent = ({
  status,
  showActivationModal,
  permanentlySuppressed,
}) => {
  if (permanentlySuppressed || !showActivationModal) return 'none';
  if (status === STATUS_UNLINKED) return 'initial';
  if (status === STATUS_PENDING) return 'initial';
  return 'none';
};

const shouldQueryActivationStatus = ({
  isFlagEnabled,
  isAdmin,
  hasActiveSubscription,
  enabled,
}) => {
  const isFlagEnabledNow = isFlagEnabled;

  const hasActiveSubscriptionNow = hasActiveSubscription

  return enabled && isFlagEnabledNow && isAdmin && hasActiveSubscriptionNow;
};

// Unica fuente de verdad de elegibilidad: feature flag + perfil Administrador +
// suscripcion HBO activa (/user/v1/superhighlight contiene 'suscripto_hbo') + GET /status.
// `enabled` (por defecto true) permite a un consumidor impedir la consulta por una condicion
// propia (ej. /activacionhbo sin region resoluble) sin duplicar la elegibilidad.
export const useHboActivationStatus = ({ enabled = true } = {}) => {
  const isFlagEnabled = useHboActivationFlag();
  const isAdmin = useSelector(state => Boolean(state?.user?.isAdmin));
  const hasActiveSubscription = useSelector(state => {
    return isHboSubscriptionActive(state?.user?.user_superhighlight);
  });

  const [state, setState] = useState(INITIAL_STATE);
  const canQuery = shouldQueryActivationStatus({
    isFlagEnabled,
    isAdmin,
    hasActiveSubscription,
    enabled,
  });

  const refetch = useCallback(() => {
    // WEBCL-3778: la cuenta ya fue confirmada como 'linked' en esta sesion -> no se
    // vuelve a invocar la consulta de estado
    if (hasConfirmedLinkedInSession) {
      setState(LINKED_STATE);
      return Promise.resolve({ state: LINKED_STATE, activeComponent: 'none' });
    }

    const canQueryNow = shouldQueryActivationStatus({
      isFlagEnabled,
      isAdmin,
      hasActiveSubscription,
      enabled,
    });
    

    if (!canQueryNow) {
      setState(INITIAL_STATE);
      return Promise.resolve({
        state: INITIAL_STATE,
        activeComponent: 'none',
      });
    }

    setState(prevState => ({ ...prevState, loading: true }));

    return getHboActivationStatus({region: getRegion()}).then(response => {
      const nextState = buildStateFromResponse(response);
      const nextActiveComponent = getActiveComponent(nextState);

      if (nextState.status === STATUS_LINKED) {
        hasConfirmedLinkedInSession = true;
      }

      setState(nextState);

      return {
        state: nextState,
        activeComponent: nextActiveComponent,
      };
    });
  }, [isAdmin, isFlagEnabled, hasActiveSubscription, enabled]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const activeComponent = canQuery ? getActiveComponent(state) : 'none';

  return {
    loading: state.loading,
    // Campos crudos para consumidores que NO deben respetar activation_permanently_suppressed
    // (ej. Banner Superdestacado / Menu Configuraciones, ver TBRFRE-1790 7.4.4 EXCEPCION)
    status: canQuery ? state.status : null,
    showActivationModal: canQuery && state.showActivationModal,
    permanentlySuppressed: state.permanentlySuppressed,
    activeComponent,
    refetch,
  };
};
